/**
 * TerritoryBulkPasteModal — paste pupil_name,postcode lines in bulk.
 *
 * Operator doctrine:
 *   • Visible proof, not seeded fiction. No "Use sample data" buttons.
 *   • Calm preview: parse the paste BEFORE submission (dry_run) so the
 *     user sees exactly how many rows will match.
 *   • Show every status returned by the backend (matched, ambiguous,
 *     no_match, invalid_postcode, geocoder_unavailable).
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, Sparkles, AlertCircle, Check, HelpCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  matched_and_geocoded: { label: "Geocoded", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: Check },
  matched_dry_run: { label: "Would match", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Check },
  matched_postcode_unknown: { label: "Saved (unverified)", cls: "bg-amber-100 text-amber-700 border-amber-300", icon: AlertCircle },
  no_match: { label: "No pupil match", cls: "bg-rose-100 text-rose-700 border-rose-300", icon: AlertCircle },
  ambiguous: { label: "Ambiguous name", cls: "bg-amber-100 text-amber-700 border-amber-300", icon: HelpCircle },
  invalid_postcode: { label: "Bad postcode", cls: "bg-rose-100 text-rose-700 border-rose-300", icon: AlertCircle },
  geocoder_unavailable: { label: "Geocoder offline", cls: "bg-slate-100 text-slate-700 border-slate-300", icon: AlertCircle },
};

export default function TerritoryBulkPasteModal({ open, onClose, onSuccess }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committed, setCommitted] = useState(false);

  if (!open) return null;

  const submit = async ({ dryRun }) => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/territory/pupils/bulk-postcodes`,
        { text, dry_run: dryRun },
        { withCredentials: true },
      );
      setPreview(r.data);
      if (!dryRun) {
        setCommitted(true);
        toast.success(
          `${r.data.summary.matched_and_geocoded} postcodes geocoded`,
        );
        if (onSuccess) onSuccess();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk import failed");
    } finally {
      setBusy(false);
    }
  };

  const sumKeys = preview ? Object.keys(preview.summary).filter((k) => k !== "dry_run") : [];

  return (
    <div
      data-testid="territory-bulk-paste-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[90dvh] bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 flex items-center gap-2 border-b border-slate-200 dark:border-slate-800">
          <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="flex-1">
            <p className="font-extrabold text-slate-900 dark:text-white text-base leading-tight">
              Bulk-paste pupil postcodes
            </p>
            <p className="text-[11px] text-slate-500 leading-tight">
              One per line · `Name, SW1A 1AA` (comma, tab or pipe)
            </p>
          </div>
          <button
            type="button"
            data-testid="territory-bulk-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-3">
          {!committed && (
            <textarea
              data-testid="territory-bulk-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={10}
              placeholder={"Alex Morgan, SW1A 1AA\nPriya Shah, EC2A 4DP\nJordan Taylor, M1 1AE"}
              className="w-full font-mono text-[12.5px] rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950/40 p-3 leading-relaxed text-slate-700 dark:text-slate-200"
            />
          )}

          {preview && (
            <div data-testid="territory-bulk-preview" className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {sumKeys.map((k) => (
                  <div
                    key={k}
                    className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950/40 px-2.5 py-1.5"
                  >
                    <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      {k.replace(/_/g, " ")}
                    </p>
                    <p className="text-base font-extrabold text-slate-900 dark:text-white tabular-nums">
                      {preview.summary[k]}
                    </p>
                  </div>
                ))}
              </div>
              <ul className="space-y-1 max-h-40 overflow-auto">
                {preview.results.map((r, i) => {
                  const meta = STATUS_META[r.status] || STATUS_META.no_match;
                  const Icon = meta.icon;
                  return (
                    <li
                      key={i}
                      className="text-[11.5px] flex items-center gap-2 rounded-md bg-slate-50 dark:bg-slate-950/40 px-2 py-1"
                    >
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9.5px] font-bold uppercase tracking-wider ${meta.cls}`}>
                        <Icon className="w-2.5 h-2.5" />
                        {meta.label}
                      </span>
                      <span className="truncate text-slate-700 dark:text-slate-300">
                        {r.name} · <span className="font-mono">{r.postcode}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className="px-5 py-3 flex items-center gap-2 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/40">
          {!committed ? (
            <>
              <button
                type="button"
                data-testid="territory-bulk-preview-btn"
                onClick={() => submit({ dryRun: true })}
                disabled={busy || !text.trim()}
                className="px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-[12.5px] font-bold text-slate-700 dark:text-slate-200 disabled:opacity-40"
              >
                {busy ? "Checking…" : "Preview"}
              </button>
              <button
                type="button"
                data-testid="territory-bulk-import-btn"
                onClick={() => submit({ dryRun: false })}
                disabled={busy || !text.trim()}
                className="ml-auto px-3 py-1.5 rounded-md bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[12.5px] font-bold disabled:opacity-40"
              >
                {busy ? "Importing…" : "Import"}
              </button>
            </>
          ) : (
            <button
              type="button"
              data-testid="territory-bulk-done-btn"
              onClick={onClose}
              className="ml-auto px-3 py-1.5 rounded-md bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[12.5px] font-bold"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
